import { auth } from './firebase';
import {
  Business,
  CallLog,
  Lead,
  Appointment,
  AppointmentInput,
  Voice,
  CalendarSlot,
  BusinessMember,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

// Business API
export async function listBusinesses(): Promise<Business[]> {
  const data = await fetchWithAuth('/businesses');
  return data.businesses;
}

export async function createBusiness(business: Partial<Business>): Promise<Business> {
  return fetchWithAuth('/businesses', {
    method: 'POST',
    body: JSON.stringify(business),
  });
}

export async function getBusiness(businessId: string): Promise<Business> {
  return fetchWithAuth(`/businesses/${businessId}`);
}

export async function updateBusiness(
  businessId: string,
  updates: Partial<Business>
): Promise<Business> {
  return fetchWithAuth(`/businesses/${businessId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// Calls API
export async function listCalls(
  businessId: string,
  limit?: number,
  startAfter?: string
): Promise<CallLog[]> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit.toString());
  if (startAfter) params.set('startAfter', startAfter);

  const data = await fetchWithAuth(
    `/businesses/${businessId}/calls?${params.toString()}`
  );
  return data.calls;
}

export async function getCall(businessId: string, callId: string): Promise<CallLog> {
  return fetchWithAuth(`/businesses/${businessId}/calls/${callId}`);
}

// Leads API
export async function listLeads(
  businessId: string,
  status?: string,
  limit?: number
): Promise<Lead[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (limit) params.set('limit', limit.toString());

  const data = await fetchWithAuth(
    `/businesses/${businessId}/leads?${params.toString()}`
  );
  return data.leads;
}

export async function updateLead(
  businessId: string,
  leadId: string,
  updates: Partial<Lead>
): Promise<Lead> {
  return fetchWithAuth(`/businesses/${businessId}/leads/${leadId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

// Appointments API
export async function listAppointments(
  businessId: string,
  startDate?: string,
  endDate?: string,
  status?: string
): Promise<Appointment[]> {
  const params = new URLSearchParams();
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  if (status) params.set('status', status);

  const data = await fetchWithAuth(
    `/businesses/${businessId}/appointments?${params.toString()}`
  );
  return data.appointments;
}

export async function createAppointment(
  businessId: string,
  appointment: AppointmentInput
): Promise<Appointment> {
  return fetchWithAuth(`/businesses/${businessId}/appointments`, {
    method: 'POST',
    body: JSON.stringify(appointment),
  });
}

export async function updateAppointment(
  businessId: string,
  appointmentId: string,
  updates: Partial<Appointment>
): Promise<Appointment> {
  return fetchWithAuth(`/businesses/${businessId}/appointments/${appointmentId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteAppointment(
  businessId: string,
  appointmentId: string
): Promise<void> {
  await fetchWithAuth(`/businesses/${businessId}/appointments/${appointmentId}`, {
    method: 'DELETE',
  });
}

// Availability API
export async function getAvailability(
  businessId: string,
  date: string,
  duration?: number
): Promise<CalendarSlot[]> {
  const params = new URLSearchParams();
  params.set('date', date);
  if (duration) params.set('duration', duration.toString());

  const data = await fetchWithAuth(
    `/businesses/${businessId}/availability?${params.toString()}`
  );
  return data.slots;
}

// Voices API
export async function listVoices(): Promise<Voice[]> {
  const data = await fetchWithAuth('/voices');
  return data.voices;
}

// Cache API
export async function clearCache(businessId: string): Promise<number> {
  const data = await fetchWithAuth(`/businesses/${businessId}/clear-cache`, {
    method: 'POST',
  });
  return data.cleared;
}

// Members API
export async function listMembers(businessId: string): Promise<BusinessMember[]> {
  const data = await fetchWithAuth(`/businesses/${businessId}/members`);
  return data.members;
}

export async function addMember(
  businessId: string,
  email: string,
  role: string,
  name?: string
): Promise<BusinessMember> {
  return fetchWithAuth(`/businesses/${businessId}/members`, {
    method: 'POST',
    body: JSON.stringify({ email, role, name }),
  });
}

export async function removeMember(
  businessId: string,
  memberId: string
): Promise<void> {
  await fetchWithAuth(`/businesses/${businessId}/members/${memberId}`, {
    method: 'DELETE',
  });
}

// Calendar OAuth API
export async function getCalendarOAuthUrl(businessId: string): Promise<string> {
  const data = await fetchWithAuth(`/oauth/google/url?businessId=${businessId}`);
  return data.url;
}

export async function getCalendarStatus(businessId: string): Promise<{
  connected: boolean;
  hasRefreshToken: boolean;
  connectedAt?: any;
}> {
  return fetchWithAuth(`/businesses/${businessId}/calendar/status`);
}

export async function disconnectCalendar(businessId: string): Promise<void> {
  await fetchWithAuth(`/businesses/${businessId}/calendar/disconnect`, {
    method: 'POST',
  });
}

// ==================== Billing API ====================

export interface Invoice {
  id: string;
  agencyId: string;
  businessId: string;
  type: 'one_time' | 'subscription';
  amountCents: number;
  description: string;
  interval?: 'month' | 'year';
  dueDate?: any;
  status: 'pending' | 'paid' | 'cancelled';
  createdAt: any;
  updatedAt: any;
}

export interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  description: string | null;
  created: number;
  metadata: Record<string, string>;
}

export interface Subscription {
  id: string;
  status: string;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  created: number;
  metadata: Record<string, string>;
  items: {
    id: string;
    priceId: string;
    amount: number | null;
    currency: string;
    interval?: string;
    productName?: string;
  }[];
}

export interface EmbeddedCheckoutSession {
  clientSecret: string;
  sessionId: string;
  connectedAccountId: string;
}

export async function listInvoices(
  businessId: string,
  status?: 'pending' | 'paid' | 'cancelled'
): Promise<Invoice[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);

  const data = await fetchWithAuth(
    `/businesses/${businessId}/billing/invoices?${params.toString()}`
  );
  return data.invoices;
}

export async function listPayments(
  businessId: string,
  limit?: number
): Promise<Payment[]> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit.toString());

  const data = await fetchWithAuth(
    `/businesses/${businessId}/billing/payments?${params.toString()}`
  );
  return data.payments;
}

export async function listSubscriptions(
  businessId: string,
  limit?: number
): Promise<Subscription[]> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', limit.toString());

  const data = await fetchWithAuth(
    `/businesses/${businessId}/billing/subscriptions?${params.toString()}`
  );
  return data.subscriptions;
}

export async function payInvoice(
  businessId: string,
  invoiceId: string,
  returnUrl: string
): Promise<EmbeddedCheckoutSession> {
  return fetchWithAuth(`/businesses/${businessId}/billing/invoices/${invoiceId}/pay`, {
    method: 'POST',
    body: JSON.stringify({ returnUrl }),
  });
}

export async function getCheckoutSessionStatus(
  businessId: string,
  sessionId: string
): Promise<{ status: string; customerEmail?: string }> {
  return fetchWithAuth(`/businesses/${businessId}/billing/session/${sessionId}/status`);
}
